import type { WorkspacesGateway } from '../application/workspacesGateway';
import type { AccessResult } from '../domain/access';
import type {
  ConversationMessage,
  ConversationMessagesResult,
  DealBoardBucket,
  DealBoardBucketResult,
  DealBoardCandidatesResult,
  DealBoardInviteCandidate,
  DealBoardItem,
  DealBoardMember,
  DealBoardMembersResult,
  DealBoardMutationResult,
  SendMessageResult,
  WorkspaceConversation,
  WorkspaceConversationsResult,
  WorkspaceOverviewResult,
  WorkspaceSummary,
  WorkspaceVector,
  WorkspaceVectorsResult,
} from '../domain/workspaces';
import type { SupabaseAccessGateway } from './supabaseAccessGateway';

const sessionKey = 'alpha-mls:supabase-session:v1';
const env = import.meta.env as Record<string, string | undefined>;

interface StoredSession {
  readonly accessToken: string;
  readonly expiresAt: number;
}

interface WorkspaceSummaryRow {
  readonly workspace_id: string;
  readonly name: string;
  readonly kind: string;
  readonly status: string;
  readonly vector_count: number | string;
  readonly conversation_count: number | string;
  readonly city: string | null;
  readonly state: string | null;
  readonly latitude: number | null;
  readonly longitude: number | null;
  readonly last_activity: string;
}

interface WorkspaceVectorRow {
  readonly vector_id: string;
  readonly vector_status: string;
  readonly score: number | string | null;
  readonly notes: string | null;
  readonly listing_id: string;
  readonly title: string;
  readonly transaction_type: 'sale' | 'rent';
  readonly price: number | string;
  readonly currency: string;
  readonly property_type: string;
  readonly city: string;
  readonly state: string;
  readonly latitude: number | null;
  readonly longitude: number | null;
  readonly client_name: string;
  readonly stage_name: string | null;
  readonly stage_key: string | null;
}

interface WorkspaceConversationRow {
  readonly conversation_id: string;
  readonly title: string;
  readonly status: string;
  readonly participant_count: number | string;
  readonly last_message: string | null;
  readonly last_message_at: string | null;
}

interface ConversationMessageRow {
  readonly message_id: string;
  readonly body: string;
  readonly message_type: string;
  readonly created_at: string;
  readonly sender_agent_id: string;
  readonly sender_name: string;
  readonly is_mine: boolean;
}

interface DealBoardItemRow {
  readonly bucket: DealBoardBucket;
  readonly source_id: string;
  readonly property_vector_id: string;
  readonly workspace_id: string;
  readonly listing_id: string;
  readonly title: string;
  readonly status: string;
  readonly provenance: string;
  readonly counterparty_agent_id: string | null;
  readonly counterparty_name: string | null;
  readonly conversation_id: string | null;
  readonly customer_name: string | null;
  readonly updated_at: string;
}

interface InviteCandidateRow {
  readonly agent_id: string;
  readonly public_name: string;
  readonly headline: string | null;
  readonly city: string;
  readonly state: string;
  readonly agency_name: string | null;
  readonly professional_role: string | null;
}

interface MemberRow {
  readonly agent_id: string;
  readonly public_name: string;
  readonly agency_name: string | null;
  readonly professional_role: string | null;
  readonly phone: string | null;
  readonly email: string | null;
  readonly license_number: string | null;
  readonly is_me: boolean;
}


function readAccessToken(): string | null {
  try {
    const raw = window.localStorage.getItem(sessionKey);
    if (raw === null) return null;

    const parsed = JSON.parse(raw) as Partial<StoredSession>;
    if (
      typeof parsed.accessToken !== 'string' ||
      typeof parsed.expiresAt !== 'number' ||
      parsed.expiresAt <= Math.floor(Date.now() / 1000)
    ) {
      return null;
    }

    return parsed.accessToken;
  } catch {
    return null;
  }
}

function numberOrNull(value: number | string | null): number | null {
  if (value === null) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

export class SupabaseWorkspacesGateway implements WorkspacesGateway {
  constructor(private readonly accessGateway: SupabaseAccessGateway) {}

  resolveCurrentIdentity(signal?: AbortSignal): Promise<AccessResult> {
    return this.accessGateway.resolveCurrentIdentity(signal);
  }

  signOut(signal?: AbortSignal): Promise<void> {
    return this.accessGateway.signOut(signal);
  }

  private async rpc<T>(
    functionName: string,
    body: Record<string, unknown>,
    signal?: AbortSignal,
  ): Promise<T | null> {
    const supabaseUrl = env.VITE_SUPABASE_URL?.replace(/\/$/u, '');
    const publishableKey = env.VITE_SUPABASE_PUBLISHABLE_KEY;
    const accessToken = readAccessToken();

    if (
      supabaseUrl === undefined ||
      publishableKey === undefined ||
      accessToken === null
    ) {
      return null;
    }

    const response = await fetch(
      `${supabaseUrl}/rest/v1/rpc/${functionName}`,
      {
        method: 'POST',
        headers: {
          apikey: publishableKey,
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(body),
        ...(signal === undefined ? {} : { signal }),
      },
    );

    if (!response.ok) return null;
    return (await response.json()) as T;
  }

  async loadOverview(signal?: AbortSignal): Promise<WorkspaceOverviewResult> {
    try {
      const rows = await this.rpc<WorkspaceSummaryRow[]>(
        'workspace_overview_v1',
        {},
        signal,
      );

      if (rows === null) {
        return { ok: false, message: 'No fue posible leer WorkSpaces.' };
      }

      const workspaces: WorkspaceSummary[] = rows.map((row) => ({
        workspaceId: row.workspace_id,
        name: row.name,
        kind: row.kind,
        status: row.status,
        vectorCount: Number(row.vector_count),
        conversationCount: Number(row.conversation_count),
        city: row.city,
        state: row.state,
        latitude: row.latitude,
        longitude: row.longitude,
        lastActivity: row.last_activity,
      }));

      return { ok: true, workspaces };
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') throw error;
      return { ok: false, message: 'No fue posible conectar con WorkSpaces.' };
    }
  }

  async loadVectors(
    workspaceId: string,
    signal?: AbortSignal,
  ): Promise<WorkspaceVectorsResult> {
    try {
      const rows = await this.rpc<WorkspaceVectorRow[]>(
        'workspace_vectors_v1',
        { p_workspace_id: workspaceId },
        signal,
      );

      if (rows === null) {
        return { ok: false, message: 'No fue posible leer los Property Vectors.' };
      }

      const vectors: WorkspaceVector[] = rows.map((row) => ({
        vectorId: row.vector_id,
        vectorStatus: row.vector_status,
        score: numberOrNull(row.score),
        notes: row.notes,
        listingId: row.listing_id,
        title: row.title,
        transactionType: row.transaction_type,
        price: Number(row.price),
        currency: row.currency.trim(),
        propertyType: row.property_type,
        city: row.city,
        state: row.state,
        latitude: row.latitude,
        longitude: row.longitude,
        clientName: row.client_name,
        stageName: row.stage_name,
        stageKey: row.stage_key,
      }));

      return { ok: true, vectors };
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') throw error;
      return { ok: false, message: 'No fue posible leer los Property Vectors.' };
    }
  }

  async loadConversations(
    workspaceId: string,
    signal?: AbortSignal,
  ): Promise<WorkspaceConversationsResult> {
    try {
      const rows = await this.rpc<WorkspaceConversationRow[]>(
        'workspace_conversations_v1',
        { p_workspace_id: workspaceId },
        signal,
      );

      if (rows === null) {
        return { ok: false, message: 'No fue posible leer las conversaciones.' };
      }

      const conversations: WorkspaceConversation[] = rows.map((row) => ({
        conversationId: row.conversation_id,
        title: row.title,
        status: row.status,
        participantCount: Number(row.participant_count),
        lastMessage: row.last_message,
        lastMessageAt: row.last_message_at,
      }));

      return { ok: true, conversations };
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') throw error;
      return { ok: false, message: 'No fue posible leer las conversaciones.' };
    }
  }

  async loadBucket(
    bucket: DealBoardBucket,
    workspaceId: string | null = null,
    signal?: AbortSignal,
  ): Promise<DealBoardBucketResult> {
    try {
      const rows = await this.rpc<DealBoardItemRow[]>(
        'dealboard_bucket_v1',
        { p_bucket: bucket, p_workspace_id: workspaceId },
        signal,
      );

      if (rows === null) {
        return { ok: false, message: 'No fue posible leer esta bandeja de DealBoard.' };
      }

      const items: DealBoardItem[] = rows.map((row) => ({
        bucket: row.bucket,
        sourceId: row.source_id,
        propertyVectorId: row.property_vector_id,
        workspaceId: row.workspace_id,
        listingId: row.listing_id,
        title: row.title,
        status: row.status,
        provenance: row.provenance,
        counterpartyAgentId: row.counterparty_agent_id,
        counterpartyName: row.counterparty_name,
        conversationId: row.conversation_id,
        customerName: row.customer_name,
        updatedAt: row.updated_at,
      }));

      return { ok: true, items };
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') throw error;
      return { ok: false, message: 'No fue posible conectar con DealBoard.' };
    }
  }

  async loadInviteCandidates(
    query = '',
    signal?: AbortSignal,
  ): Promise<DealBoardCandidatesResult> {
    try {
      const rows = await this.rpc<InviteCandidateRow[]>(
        'dealboard_invite_candidates_v1',
        { p_query: query.trim() === '' ? null : query.trim(), p_limit: 24 },
        signal,
      );

      if (rows === null) {
        return { ok: false, message: 'No fue posible leer profesionales de RED.' };
      }

      const candidates: DealBoardInviteCandidate[] = rows.map((row) => ({
        agentId: row.agent_id,
        publicName: row.public_name,
        headline: row.headline,
        city: row.city,
        state: row.state,
        agencyName: row.agency_name,
        professionalRole: row.professional_role,
      }));

      return { ok: true, candidates };
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') throw error;
      return { ok: false, message: 'No fue posible conectar con RED.' };
    }
  }

  async loadMembers(
    conversationId: string,
    signal?: AbortSignal,
  ): Promise<DealBoardMembersResult> {
    try {
      const rows = await this.rpc<MemberRow[]>(
        'dealboard_members_v1',
        { p_conversation_id: conversationId },
        signal,
      );

      if (rows === null) {
        return { ok: false, message: 'No fue posible leer los participantes.' };
      }

      const members: DealBoardMember[] = rows.map((row) => ({
        agentId: row.agent_id,
        publicName: row.public_name,
        agencyName: row.agency_name,
        professionalRole: row.professional_role,
        phone: row.phone,
        email: row.email,
        licenseNumber: row.license_number,
        isMe: row.is_me,
      }));

      return { ok: true, members };
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') throw error;
      return { ok: false, message: 'No fue posible conectar con participantes.' };
    }
  }

  async respondAuthorization(
    requestId: string,
    decision: 'authorized' | 'ignored' | 'declined',
    signal?: AbortSignal,
  ): Promise<DealBoardMutationResult> {
    try {
      const payload = await this.rpc<Record<string, unknown>>(
        'dealboard_respond_authorization_v1',
        { p_request_id: requestId, p_decision: decision },
        signal,
      );
      return payload === null
        ? { ok: false, message: 'No fue posible registrar la decisión.' }
        : { ok: true, payload };
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') throw error;
      return { ok: false, message: 'No fue posible registrar la decisión.' };
    }
  }

  async createInvitation(
    listingIds: readonly string[],
    agentIds: readonly string[],
    signal?: AbortSignal,
  ): Promise<DealBoardMutationResult> {
    try {
      const payload = await this.rpc<Record<string, unknown>>(
        'dealboard_invite_v1',
        { p_listing_ids: listingIds, p_agent_ids: agentIds },
        signal,
      );
      return payload === null
        ? { ok: false, message: 'No fue posible crear la invitación.' }
        : { ok: true, payload };
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') throw error;
      return { ok: false, message: 'No fue posible crear la invitación.' };
    }
  }

  async loadMessages(
    conversationId: string,
    signal?: AbortSignal,
  ): Promise<ConversationMessagesResult> {
    try {
      const rows = await this.rpc<ConversationMessageRow[]>(
        'conversation_messages_v1',
        {
          p_conversation_id: conversationId,
          p_before: null,
          p_limit: 60,
        },
        signal,
      );

      if (rows === null) {
        return { ok: false, message: 'No fue posible leer el hilo.' };
      }

      const messages: ConversationMessage[] = rows
        .map((row) => ({
          messageId: row.message_id,
          body: row.body,
          messageType: row.message_type,
          createdAt: row.created_at,
          senderAgentId: row.sender_agent_id,
          senderName: row.sender_name,
          isMine: row.is_mine,
        }))
        .reverse();

      return { ok: true, messages };
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') throw error;
      return { ok: false, message: 'No fue posible leer el hilo.' };
    }
  }

  async sendMessage(
    conversationId: string,
    body: string,
    signal?: AbortSignal,
  ): Promise<SendMessageResult> {
    try {
      const messageId = await this.rpc<string>(
        'workspace_send_message_v1',
        { p_conversation_id: conversationId, p_body: body },
        signal,
      );

      if (messageId === null) {
        return { ok: false, message: 'No fue posible enviar el mensaje.' };
      }

      return { ok: true, messageId };
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') throw error;
      return { ok: false, message: 'No fue posible enviar el mensaje.' };
    }
  }
}
